import { formatMoney } from '@theme/money-formatting';

/**
 * Suri-style choice on a device product: subscribe to replacement heads, or buy the device alone.
 */
class ReplacementPlan extends HTMLElement {
  /** @type {Array<{id: number, available: boolean, options: string[], price: number, compareAt: number, image: string}>} */
  #variants = [];

  /** @type {{id: number, available: boolean, options: string[], price: number, compareAt: number, image: string} | null} */
  #matched = null;

  /** @type {number} */
  #devicePrice = 0;

  /** @type {string[]} */
  #options = [];

  connectedCallback() {
    const data = this.querySelector('[data-variants]')?.textContent;
    this.#variants = data ? JSON.parse(data) : [];
    this.#devicePrice = Number(this.dataset.devicePrice) || 0;
    this.#options = this.dataset.options ? JSON.parse(this.dataset.options) : [];

    this.addEventListener('change', () => {
      this.removeAttribute('data-choice-missing');
      this.#hideMessage();
      this.#render();
    });

    document.addEventListener('variant:update', this.#onVariantUpdate);
    this.#render();
    if (!this.dataset.sellingPlanId && this.dataset.headsHandle) {
      this.#loadSellingPlan();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  /**
   * @returns {{type: 'none'} | {type: 'unavailable'} | {type: 'subscribe', variantId: number, quantity: number, sellingPlanId: string} | null}
   */
  getChoice() {
    const selected = this.querySelector('input[type="radio"]:checked');
    if (!(selected instanceof HTMLInputElement)) return null;
    if (selected.value === 'none') return { type: 'none' };

    const variant = this.#matchVariant(this.#options);
    if (!variant || !variant.available) return { type: 'unavailable' };

    return {
      type: 'subscribe',
      variantId: variant.id,
      quantity: Number(this.dataset.quantity) || 1,
      sellingPlanId: this.dataset.sellingPlanId || '',
    };
  }

  showChoiceRequired() {
    this.setAttribute('data-choice-missing', 'true');
    this.#showMessage(this.dataset.requiredMessage || 'Choose a plan to continue.');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showUnavailable() {
    this.#showMessage(this.dataset.unavailableMessage || 'Replacement heads are currently unavailable.');
  }

  async #loadSellingPlan() {
    const root = window.Shopify?.routes?.root || '/';
    const handle = this.dataset.headsHandle;
    try {
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) return;
      const product = await response.json();
      const plan = product.selling_plan_groups?.[0]?.selling_plans?.[0];
      if (!plan) {
        this.#showMessage(`${product.title} has no selling plan. The Seal rule has to be on this product.`);
        return;
      }

      this.dataset.sellingPlanId = String(plan.id);
      const featured =
        typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src || '';
      this.#variants = (product.variants || []).map((variant) => {
        const allocation = (variant.selling_plan_allocations || []).find(
          (item) => String(item.selling_plan_id) === String(plan.id)
        );
        return {
          id: variant.id,
          available: variant.available,
          options: [variant.option1, variant.option2, variant.option3].filter(Boolean),
          price: allocation?.price ?? variant.price,
          compareAt: variant.price,
          image: variant.featured_image?.src || featured,
        };
      });
      this.#hideMessage();
      this.#render();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const productId = event.detail?.data?.productId;
    if (productId != null && String(productId) !== this.dataset.productId) return;

    const resource = event.detail?.resource;
    if (resource) {
      if (resource.price != null) this.#devicePrice = Number(resource.price);
      this.#options = [resource.option1, resource.option2, resource.option3].filter((value) => value);
    }

    requestAnimationFrame(() => this.#render());
  };

  #render() {
    const devicePrice = Number(this.dataset.devicePrice);
    if (devicePrice) this.#devicePrice = devicePrice;

    const variant = this.#matchVariant(this.#options);
    this.#matched = variant;
    const quantity = Number(this.dataset.quantity) || 1;

    const image = this.querySelector('[data-plan-image]');
    if (image instanceof HTMLImageElement && variant?.image) {
      image.src = variant.image;
      image.hidden = false;
    }

    const priceEl = this.querySelector('[data-plan-price]');
    const compareEl = this.querySelector('[data-plan-compare]');
    if (variant && priceEl) {
      const linePrice = variant.price * quantity;
      const comparePrice = variant.compareAt * quantity;
      priceEl.textContent = this.#money(linePrice);
      if (compareEl) {
        if (comparePrice > linePrice) {
          compareEl.hidden = false;
          compareEl.textContent = this.#money(comparePrice);
        } else {
          compareEl.hidden = true;
        }
      }
    }

    const choice = this.getChoice();
    const total = this.querySelector('[data-plan-total]');
    if (total instanceof HTMLElement) {
      const showTotal = choice?.type === 'subscribe' && variant;
      total.hidden = !showTotal;
      if (showTotal && variant) {
        const renewal = variant.price * quantity;
        const today = this.querySelector('[data-plan-total-today]');
        const renewalEl = this.querySelector('[data-plan-renewal]');
        if (today) today.textContent = this.#money(this.#devicePrice + renewal);
        if (renewalEl) {
          renewalEl.textContent = `+ ${this.#money(renewal)} ${this.dataset.cadence || ''}`.trim();
        }
      }
    }

    this.#setButtonLabel(choice);
  }

  /**
   * @param {ReturnType<ReplacementPlan['getChoice']>} choice
   */
  #setButtonLabel(choice) {
    const button = this.closest('.shopify-section')?.querySelector(
      'product-form-component [ref="addToCartButton"]'
    );
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    let label = this.dataset.labelChoose || 'Pick your plan';
    if (choice?.type === 'subscribe') label = this.dataset.labelSubscribe || 'Subscribe and save';
    if (choice?.type === 'none') label = this.dataset.labelAdd || 'Add to cart';

    const section = this.closest('.shopify-section');
    section?.querySelectorAll('.add-to-cart-text__content').forEach((node) => {
      const target = node.querySelector('span span') || node;
      target.textContent = label;
    });
  }

  /**
   * @param {string[]} options
   */
  #matchVariant(options) {
    const wanted = options.filter(Boolean).map((value) => value.toLowerCase());
    const available = this.#variants.filter((variant) => variant.available);
    const pool = available.length > 0 ? available : this.#variants;

    const exact = pool.find((variant) =>
      wanted.every((value) => variant.options.some((option) => option && option.toLowerCase() === value))
    );

    return exact || pool[0] || null;
  }

  /**
   * @param {number} cents
   */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'USD');
  }

  /**
   * @param {string} message
   */
  #showMessage(message) {
    const messageEl = this.querySelector('[data-plan-message]');
    if (!(messageEl instanceof HTMLElement)) return;
    messageEl.hidden = false;
    messageEl.textContent = message;
  }

  #hideMessage() {
    const messageEl = this.querySelector('[data-plan-message]');
    if (messageEl instanceof HTMLElement) messageEl.hidden = true;
  }
}

if (!customElements.get('bb-replacement-plan')) {
  customElements.define('bb-replacement-plan', ReplacementPlan);
}

const TOOTHBRUSH_PLAN = {
  headsHandle:
    'barberboss-qr-7301-replacement-toothbrush-heads-4-standard-heads-and-2-mini-heads-compatible-with-barberboss-electric-toothbrush-pack-of-6',
  devices: [],
  quantity: 1,
  subscribeLabel: 'Subscribe and save 15%',
  badge: 'Best value',
  detail: 'Pack of 6 · Every 6 months',
  note: 'Pause or cancel anytime.',
  cadence: 'Every 6 months',
  declineTitle: 'No extra heads',
  declineText: 'The toothbrush already includes 6 heads.',
};

/**
 * @param {string} value
 */
function escapePlanText(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Plans come from the Replacement plans section in the theme editor.
 * Until that section is on the page, the original toothbrush plan still shows.
 * @param {string} handle
 */
function planForProduct(handle) {
  const node = document.getElementById('bb-replacement-plans');
  if (!(node instanceof HTMLElement)) {
    if (!handle.includes('toothbrush') || handle.includes('replacement')) return null;
    return TOOTHBRUSH_PLAN;
  }

  /** @type {Array<typeof TOOTHBRUSH_PLAN>} */
  let plans = [];
  try {
    plans = JSON.parse(node.textContent || '[]');
  } catch {
    plans = [];
  }

  if (plans.some((plan) => String(plan.headsHandle || '').toLowerCase() === handle)) return null;

  return (
    plans.find(
      (plan) =>
        plan.headsHandle &&
        Array.isArray(plan.devices) &&
        plan.devices.some((device) => String(device).toLowerCase() === handle)
    ) || null
  );
}

/**
 * The theme block often renders nothing, so the script draws the box on device pages.
 */
function mountReplacementPlan() {
  const form = document.querySelector('product-form-component');
  if (!(form instanceof HTMLElement)) return;

  const productPath = (form.dataset.productUrl || window.location.pathname).toLowerCase();
  const handle = decodeURIComponent(productPath.split('/products/')[1] || '').split('/')[0];
  const config = planForProduct(handle);
  const editorList = document.getElementById('bb-replacement-plans');
  const existing = document.querySelector('bb-replacement-plan');

  if (!config) {
    if (editorList && existing) existing.remove();
    return;
  }

  if (existing) existing.remove();

  if (!document.getElementById('bb-replacement-plan-style')) {
    const style = document.createElement('style');
    style.id = 'bb-replacement-plan-style';
    style.textContent = `
      bb-replacement-plan.bb-replacement-plan { display: block; width: 100%; max-width: 100%; min-width: 0; align-self: stretch; box-sizing: border-box; margin: 2px 0 18px; color: #0e2947; text-align: left; }
      .shopify-section:has(bb-replacement-plan) .accelerated-checkout-block { display: none; }
      bb-replacement-plan .bb-replacement-plan__fieldset { display: flex; flex-direction: column; align-items: stretch; gap: 10px; width: 100%; max-width: 100%; min-width: 0; margin: 0; padding: 0; border: 0; box-sizing: border-box; }
      bb-replacement-plan .bb-replacement-plan__heading { margin: 0 0 2px; padding: 0; font-size: 0.95rem; font-weight: 650; line-height: 1.3; text-align: left; }
      bb-replacement-plan .bb-replacement-plan__option { display: block; width: 100%; max-width: 100%; min-width: 0; margin: 0; box-sizing: border-box; cursor: pointer; }
      bb-replacement-plan .bb-replacement-plan__input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
      bb-replacement-plan .bb-replacement-plan__card { display: flex; flex-direction: column; gap: 6px; width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box; padding: 14px; border: 1.5px solid #d5dee8; border-radius: 12px; background: #fff; text-align: left; }
      bb-replacement-plan .bb-replacement-plan__input:checked + .bb-replacement-plan__card { border-color: #0e2947; background: #f4f8fb; box-shadow: inset 0 0 0 1px #0e2947; }
      bb-replacement-plan .bb-replacement-plan__input:focus-visible + .bb-replacement-plan__card { outline: 2px solid #0e2947; outline-offset: 2px; }
      bb-replacement-plan[data-choice-missing='true'] .bb-replacement-plan__card { border-color: #9a3412; }
      bb-replacement-plan .bb-replacement-plan__head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px; min-width: 0; }
      bb-replacement-plan .bb-replacement-plan__name { min-width: 0; font-size: 0.95rem; font-weight: 650; line-height: 1.3; }
      bb-replacement-plan .bb-replacement-plan__radio { width: 18px; height: 18px; flex: 0 0 18px; border: 1.5px solid #8aa0b8; border-radius: 50%; background: #fff; position: relative; }
      bb-replacement-plan .bb-replacement-plan__input:checked + .bb-replacement-plan__card .bb-replacement-plan__radio { border-color: #0e2947; background: #0e2947; }
      bb-replacement-plan .bb-replacement-plan__input:checked + .bb-replacement-plan__card .bb-replacement-plan__radio::after { content: ""; position: absolute; inset: 4px; border-radius: 50%; background: #fff; }
      bb-replacement-plan .bb-replacement-plan__badge { flex: 0 0 auto; padding: 3px 8px; border-radius: 999px; background: #0e2947; color: #fff; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.02em; line-height: 1.3; white-space: nowrap; }
      bb-replacement-plan .bb-replacement-plan__price-row { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; margin-left: auto; line-height: 1.2; }
      bb-replacement-plan .bb-replacement-plan__price { font-weight: 700; font-size: 0.95rem; }
      bb-replacement-plan .bb-replacement-plan__compare { color: #7b8b9a; font-size: 0.8rem; }
      bb-replacement-plan .bb-replacement-plan__details { display: flex; flex-direction: column; gap: 2px; margin-left: 28px; color: #3d5166; font-size: 0.86rem; line-height: 1.35; }
      bb-replacement-plan .bb-replacement-plan__message { margin: 8px 0 0; color: #9a3412; font-size: 0.9rem; }
      bb-replacement-plan .bb-replacement-plan__total { margin-top: 4px; padding-top: 12px; border-top: 1px solid #e2e8ef; }
      bb-replacement-plan .bb-replacement-plan__total-row { display: flex; justify-content: space-between; gap: 12px; font-weight: 700; }
      bb-replacement-plan .bb-replacement-plan__renewal { margin: 4px 0 0; color: #3d5166; font-size: 0.86rem; }
    `;
    document.head.appendChild(style);
  }

  const plan = document.createElement('bb-replacement-plan');
  plan.className = 'bb-replacement-plan';
  plan.dataset.productId = form.dataset.productId || '';
  plan.dataset.devicePrice = '0';
  plan.dataset.options = '[]';
  plan.dataset.quantity = String(config.quantity || 1);
  plan.dataset.headsHandle = config.headsHandle;
  plan.dataset.cadence = config.cadence || 'Every 6 months';
  plan.dataset.moneyFormat = '£{{amount}}';
  plan.dataset.currency = window.Shopify?.currency?.active || 'GBP';
  plan.dataset.labelChoose = 'Pick your plan';
  plan.dataset.labelSubscribe = 'Subscribe and save';
  plan.dataset.labelAdd = 'Add to cart';
  plan.dataset.requiredMessage = 'Choose a plan to continue.';
  plan.dataset.unavailableMessage = 'Replacement heads are currently unavailable.';
  plan.innerHTML = `
    <fieldset class="bb-replacement-plan__fieldset">
      <legend class="bb-replacement-plan__heading">Pick your plan</legend>
      <label class="bb-replacement-plan__option bb-replacement-plan__option--subscribe">
        <input class="bb-replacement-plan__input" type="radio" name="bb-replacement-plan" value="subscribe">
        <span class="bb-replacement-plan__card">
          <span class="bb-replacement-plan__head">
            <span class="bb-replacement-plan__radio" aria-hidden="true"></span>
            <span class="bb-replacement-plan__name">${escapePlanText(config.subscribeLabel || 'Subscribe and save 15%')}</span>
            ${config.badge ? `<span class="bb-replacement-plan__badge">${escapePlanText(config.badge)}</span>` : ''}
            <span class="bb-replacement-plan__price-row">
              <span class="bb-replacement-plan__price" data-plan-price></span>
              <s class="bb-replacement-plan__compare" data-plan-compare hidden></s>
            </span>
          </span>
          <span class="bb-replacement-plan__details">
            <span class="bb-replacement-plan__cadence">${escapePlanText(config.detail || '')}</span>
            <span class="bb-replacement-plan__note">${escapePlanText(config.note || '')}</span>
          </span>
        </span>
      </label>
      <label class="bb-replacement-plan__option">
        <input class="bb-replacement-plan__input" type="radio" name="bb-replacement-plan" value="none">
        <span class="bb-replacement-plan__card">
          <span class="bb-replacement-plan__head">
            <span class="bb-replacement-plan__radio" aria-hidden="true"></span>
            <span class="bb-replacement-plan__name">${escapePlanText(config.declineTitle || 'No extra heads')}</span>
          </span>
          <span class="bb-replacement-plan__details">
            <span class="bb-replacement-plan__note">${escapePlanText(config.declineText || '')}</span>
          </span>
        </span>
      </label>
    </fieldset>
    <p class="bb-replacement-plan__message" data-plan-message hidden></p>
    <div class="bb-replacement-plan__total" data-plan-total hidden>
      <div class="bb-replacement-plan__total-row">
        <span>Your total today</span>
        <span data-plan-total-today></span>
      </div>
      <p class="bb-replacement-plan__renewal" data-plan-renewal></p>
    </div>
  `;

  const anchor = form.closest('.buy-buttons-block') || form;
  anchor.before(plan);

  const root = window.Shopify?.routes?.root || '/';
  fetch(`${root}products/${handle}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      if (!product) return;
      plan.dataset.devicePrice = String(product.price || 0);
      plan.dataset.productId = String(product.id || plan.dataset.productId);
      const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
      if (variant) {
        plan.dataset.options = JSON.stringify(
          [variant.option1, variant.option2, variant.option3].filter(Boolean)
        );
      }
      plan.dispatchEvent(new Event('change'));
    })
    .catch(() => {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountReplacementPlan);
} else {
  mountReplacementPlan();
}
